import type { AxiosRequestConfig } from 'axios'
import { isAxiosError } from 'axios'
import type { Merge } from 'type-fest'
import type {
    AccountInfoResponse,
    ClientStats,
    CreateJobProperties,
    DataProtectionConsentData,
    DeletedFile,
    DeletedFileResponse,
    ExtraJobQueryParamsOf,
    JobChange,
    JobFile,
    JobInfoResponse,
    StoryJobInfoResponse,
    UserAccountAttributeKey,
    UpsertUserAccountAttribute,
    JobListResponse,
    UserGrant,
    UserAccountAttribute,
    DataProtectionUpdateRequestResult,
    CollectionClientEventData,
    MemoryClientEventData,
    ChangesAndAsyncUploadStatus,
} from '~/@types/backend-types'
import { PRODUCT_NAME, getCurrentLangDefinition } from '~/config/constants'
import { canReadFileContent } from '~/state/uploader/uploadFileURL'
import { UploadError, UploadFailedReason } from '~/state/uploader/uploadQueue'
import { chunks } from '~/utilities/arrayUtils'
import { managedPromiseAll } from '~/utilities/promises'

import type {
    CommentResponseDTO,
    DeleteAlbumsResponseDTO,
    JobSetPermissionsResponseDTO,
    PartialMetadataResponseDTO,
    RestoreAlbumsResponseDTO,
    FileActionMultiResponseDTO,
    SetReactionResponseDTO,
    TrashcanAlbumItemDTO,
    UsersResponseDTO,
    PublishShareResponseDTO,
    TimelineMonthsDTO,
    PostRequestJobsPublishByJobUuidParams,
    GetRequestJobsFilesByJobUuidParams,
    PostRequestJobsFilesDedupByJobUuidParams,
    PostRequestJobsUploadByJobUuid200,
    PostRequestJobsPermissionsByJobUuidParams,
    PrivacyModeDTO,
    DataProtectionRequestResultDTO,
    DigitalLegacyChoiceDTO,
    DigitalLegacyContactDTO,
    LanguageDTO,
    DigitalLegacyInfoDTO,
    CollectionTypeDTO,
    FullScreenCardsResponseV2DTO,
    PostRequestUserStatisticsEventsBodiesEventsItem,
    FileObjectResponseDTO,
} from '@capture/client-api/src/orval'
import {
    ClientEventTypeDTO,
    ReactionTypeDTO,
    FileActionStatusDTO,
    UploadPolicyDTO,
    deleteRequestAccountAttribute,
    deleteRequestDeleteAlbumsFromTrashCan,
    deleteRequestDevicesByDeviceId,
    deleteRequestJobsByJobUuid,
    deleteRequestJobsFilesByIdByJobUuidAndFileUuid,
    deleteRequestJobsFilesByIdCommentsByJobUuidAndFileUuidAndCommentUuid,
    deleteRequestJobsFilesByIdReactionByJobUuidAndFileUuid,
    deleteRequestOptionsByName,
    getRequestAccountAttribute,
    getRequestAccountInfo,
    getRequestDataProtectionConsentValues,
    getRequestDataProtectionRequestDataAccessAvailability,
    getRequestDevices,
    getRequestDigitalLegacy,
    getRequestJobs,
    getRequestJobsChangesAndAsyncUploadStatusByJobUuid,
    getRequestJobsChangesByJobUuid,
    getRequestJobsDefault,
    getRequestJobsFilesByIdByJobUuidAndFileUuid,
    getRequestJobsFilesByJobUuid,
    getRequestJobsInfoByJobUuid,
    getRequestJobsMetadataByJobUuidAndFileUuid,
    getRequestJobsTimelineMonthsByJobUuid,
    getRequestJobsUsersByJobUuid,
    getRequestOptionsByName,
    getRequestSupportedExtensions,
    getRequestTestEmailConsentId,
    getRequestTrashCan,
    getRequestTrashCanAlbums,
    getRequestUserGrants,
    getRequestV2FullScreenCards,
    postRequestAccountAttribute,
    postRequestApproveTos,
    postRequestClientEvent,
    postRequestClientStats,
    postRequestDataProtectionConsentValues,
    postRequestDataProtectionRequestAccountDeletion,
    postRequestDigitalLegacy,
    postRequestEmptyTrashCan,
    postRequestFullScreenCardsDismissByCardId,
    postRequestJobs,
    postRequestJobsCoverByJobUuid,
    postRequestJobsFilesByIdCommentsByJobUuidAndFileUuid,
    postRequestJobsFilesByIdReactionByJobUuidAndFileUuid,
    postRequestJobsFilesDedupByJobUuid,
    postRequestJobsKeepAllFilesByJobUuid,
    postRequestJobsKeepFilesByJobUuid,
    postRequestJobsMultiRollbackByJobUuid,
    postRequestJobsNameByJobUuid,
    postRequestJobsPermissionsByJobUuid,
    postRequestJobsPrivacyModeByJobUuid,
    postRequestJobsPublishByJobUuid,
    postRequestJobsSubscribeByJobUuid,
    postRequestJobsUnsubscribeByJobUuid,
    postRequestJobsUploadByJobUuid,
    postRequestLogout,
    postRequestName,
    postRequestRollbackAlbums,
    postRequestTestDeleteStripeData,
    postRequestTestJobsMemoryCollectionsByJobUuidAndCollectionType,
    postRequestUserStatisticsEvents,
    putRequestJobsAttributeByJobUuidAndAttribute,
    putRequestJobsFilesByIdCommentsByJobUuidAndFileUuidAndCommentUuid,
    putRequestOptionsByName,
} from '@capture/client-api/src/orval'
import { customInstance } from '@capture/client-api/src/orval/customInstance'

import { type ServiceDict } from '../externals'
import { HostUrl, customFetch, downloadThroughAnchor, sendPOSTRedirect } from '../toolbox'

import { APIService } from './APIService'

export class AppService extends APIService {
    private hostUrl: HostUrl
    private downloadHost: HostUrl

    constructor(services: ServiceDict, authToken: string | undefined) {
        super('appHost', services, authToken)

        const { commonParams } = this.getRequestContext()

        // we maintain the authToken here for the endpoints which are not migrated
        // and still use the host provider
        this.hostUrl = new HostUrl(services.appHost, {
            ...commonParams,
            auth: authToken,
        })
        this.downloadHost = new HostUrl(services.downloadHost, {
            ...commonParams,
            auth: authToken,
        })
    }

    public async getAccountInfo(): Promise<AccountInfoResponse> {
        const { commonParams, requestOptions } = this.getRequestContext()
        const response = await getRequestAccountInfo(commonParams, requestOptions)

        return response.data as AccountInfoResponse
    }

    public async setProfileName(name: string) {
        const { commonParams, requestOptions } = this.getRequestContext()
        const response = await postRequestName(
            {
                ...commonParams,
                name,
            },
            requestOptions,
        )

        return response.data
    }

    public async setProfilePictureFromBlob(blob: Blob): Promise<Response> {
        const url = this.hostUrl.getPath(`/st/4/profile/picture`, {
            path: 'profile.jpg',
        })
        const payload = new FormData()
        payload.append('file', blob)

        const request = new XMLHttpRequest()
        await new Promise((success, error) => {
            request.addEventListener('load', success)
            request.addEventListener('error', error)
            request.addEventListener('abort', error)

            request.open('POST', url)
            request.send(payload)
        })
        if (request.status !== 201) {
            throw new Error(request.responseText)
        }
        return request.response
    }

    public async getUserOption(optionKey: string) {
        const { commonParams, requestOptions } = this.getRequestContext()
        const response = await getRequestOptionsByName(optionKey, commonParams, {
            ...requestOptions,
            responseType: 'text',
        })

        return response.data
    }

    public async setUserOption(optionKey: string, value: string) {
        const { commonParams, requestOptions } = this.getRequestContext()
        return putRequestOptionsByName(
            optionKey,
            {
                ...commonParams,
                value,
            },
            requestOptions,
        )
    }

    public async deleteUserOption(optionKey: string) {
        const { commonParams, requestOptions } = this.getRequestContext()
        return deleteRequestOptionsByName(optionKey, commonParams, requestOptions)
    }

    public async getConnectedDevices() {
        const { commonParams, requestOptions } = this.getRequestContext()
        const response = await getRequestDevices(commonParams, requestOptions)

        return response.data
    }

    public async deleteConnectedDevice(deviceID: string) {
        const { commonParams, requestOptions } = this.getRequestContext()
        await deleteRequestDevicesByDeviceId(deviceID, commonParams, requestOptions)
    }

    public logout() {
        const { commonParams, requestOptions } = this.getRequestContext()
        return postRequestLogout(commonParams, requestOptions)
    }

    public async getJobList(): Promise<JobListResponse> {
        const { commonParams, requestOptions } = this.getRequestContext()
        const response = await getRequestJobs(
            {
                ...commonParams,
                stories: true,
                include_details: true,
            },
            requestOptions,
        )

        return response.data as JobListResponse
    }

    public async getDefaultJob() {
        const { commonParams, requestOptions } = this.getRequestContext()
        const response = await getRequestJobsDefault(commonParams, requestOptions)

        return response.data
    }

    public async getTimelineMonths(jobID: JobID): Promise<TimelineMonthsDTO> {
        const { commonParams, requestOptions } = this.getRequestContext(jobID)
        const response = await getRequestJobsTimelineMonthsByJobUuid(
            jobID,
            commonParams,
            requestOptions,
        )

        return response.data
    }

    public async getJobInfo(jobID: JobID): Promise<JobInfoResponse> {
        const { commonParams, requestOptions } = this.getRequestContext(jobID)
        const response = await getRequestJobsInfoByJobUuid(
            jobID,
            {
                ...commonParams,
                include_details: true,
            },
            requestOptions,
        )

        return response.data as JobInfoResponse
    }

    public async getJobChanges(jobID: JobID, since: number): Promise<JobChange[]> {
        const { commonParams, requestOptions } = this.getRequestContext(jobID)
        const response = await getRequestJobsChangesByJobUuid(
            jobID,
            {
                ...commonParams,
                since,
            },
            requestOptions,
        )

        return response.data as JobChange[]
    }

    public async getJobContributors(
        jobID: JobID,
        includeProfilePicture: boolean,
    ): Promise<UsersResponseDTO> {
        const { commonParams, requestOptions } = this.getRequestContext(jobID)
        const response = await getRequestJobsUsersByJobUuid(
            jobID,
            {
                ...commonParams,
                include_profile_url: includeProfilePicture,
            },
            requestOptions,
        )

        return response.data
    }

    public async createJob(params: CreateJobProperties): Promise<StoryJobInfoResponse> {
        const { commonParams, requestOptions } = this.getRequestContext()
        const response = await postRequestJobs(
            {
                ...commonParams,
                ...params,
            },
            requestOptions,
        )

        return response.data as StoryJobInfoResponse
    }

    public async publishJob(
        jobID: JobID,
        params: ExtraJobQueryParamsOf<PostRequestJobsPublishByJobUuidParams>,
    ): Promise<PublishShareResponseDTO> {
        const { commonParams, requestOptions } = this.getRequestContext(jobID)
        const response = await postRequestJobsPublishByJobUuid(
            jobID,
            {
                ...commonParams,
                ...params,
            },
            requestOptions,
        )

        return response.data
    }

    public async deleteJob(jobID: JobID) {
        const { commonParams, requestOptions } = this.getRequestContext(jobID)
        await deleteRequestJobsByJobUuid(jobID, commonParams, requestOptions)
    }

    public async getFiles(
        jobID: JobID,
        options: ExtraJobQueryParamsOf<GetRequestJobsFilesByJobUuidParams>,
    ): Promise<{
        lastEventSerial: number
        files: JobFile[]
    }> {
        const { commonParams, requestOptions } = this.getRequestContext(jobID)
        const response = await getRequestJobsFilesByJobUuid(
            jobID,
            {
                ...commonParams,
                ...options,
            },
            requestOptions,
        )

        return {
            files: response.data as JobFile[],
            lastEventSerial: parseInt(response.headers['x-last-event-serial'] || '-1', 10),
        }
    }

    // TODO(br1anchen): use generated post request method when FastAPI doc is updated
    public async getFilesByIDs(jobID: JobID, fileIDs: FileID[]) {
        const { commonParams, requestOptions } = this.getRequestContext(jobID)
        const response = await customInstance<FileObjectResponseDTO[]>(
            {
                url: `/jobs/${encodeURIComponent(String(jobID))}/files_by_uuid`,
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                data: { file_uuids: fileIDs },
                params: commonParams,
            },
            requestOptions,
        )

        return response.data as JobFile[]
    }

    public async getDeletedFiles(offset: number, limit: number): Promise<DeletedFileResponse> {
        const { commonParams, requestOptions } = this.getRequestContext()
        const response = await getRequestTrashCan(
            {
                ...commonParams,
                total_item_count: true,
                offset,
                limit,
            },
            requestOptions,
        )

        const totalItems = response.headers['capture-total-items']

        return {
            deletedFiles: response.data as DeletedFile[],
            totalItemCount: totalItems ? parseInt(totalItems, 10) : response.data.length,
        }
    }

    public async getDeletedAlbums(): Promise<TrashcanAlbumItemDTO[]> {
        const { commonParams, requestOptions } = this.getRequestContext()
        const response = await getRequestTrashCanAlbums(commonParams, requestOptions)

        return response.data
    }

    public async getFileMetadata(
        jobID: JobID,
        fileID: FileID,
    ): Promise<PartialMetadataResponseDTO> {
        const { commonParams, requestOptions } = this.getRequestContext(jobID)
        const response = await getRequestJobsMetadataByJobUuidAndFileUuid(
            jobID,
            fileID,
            commonParams,
            requestOptions,
        )

        return response.data
    }

    public async dedupFile(
        targetJob: JobID,
        params: ExtraJobQueryParamsOf<PostRequestJobsFilesDedupByJobUuidParams>,
    ): Promise<string> {
        const { commonParams, requestOptions } = this.getRequestContext(targetJob)
        const response = await postRequestJobsFilesDedupByJobUuid(
            targetJob,
            {
                ...commonParams,
                ...params,
                want_json: true,
            },
            requestOptions,
        ).catch((_error) => {
            throw new UploadError(UploadFailedReason.FileError, 'File rejected by server')
        })

        return response.data.uuid
    }

    public async copyFilesToDefaultJob(sourceJobID: JobID, fileIDs: FileID[]): Promise<void> {
        const { commonParams, requestOptions } = this.getRequestContext(sourceJobID)
        await postRequestJobsKeepFilesByJobUuid(
            sourceJobID,
            fileIDs.join('\n'),
            commonParams,
            requestOptions,
        )
    }

    public async copyJobToDefaultJob(sourceJobID: JobID): Promise<void> {
        const { commonParams, requestOptions } = this.getRequestContext(sourceJobID)
        await postRequestJobsKeepAllFilesByJobUuid(sourceJobID, commonParams, requestOptions)
    }

    public async uploadFile(
        jobID: JobID,
        path: string,
        file: File | Blob,
        mtime: number,
        config?: AxiosRequestConfig,
    ): Promise<PostRequestJobsUploadByJobUuid200> {
        const { commonParams, requestOptions } = this.getRequestContext(jobID)
        try {
            const response = await postRequestJobsUploadByJobUuid(
                jobID,
                { file },
                {
                    ...commonParams,
                    path,
                    mtime,
                    policy: UploadPolicyDTO.no_duplicates,
                },
                {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                    ...requestOptions,
                    ...config,
                },
            )

            return response.data
        } catch (e) {
            // CAPWEB-2088: Upload can fail because local files have been removed
            if (!(await canReadFileContent(file))) {
                throw new UploadError(
                    UploadFailedReason.LocalFileUnavailable,
                    'File no longer exists',
                )
            }

            if (isAxiosError(e) && e.status === 413) {
                throw new UploadError(UploadFailedReason.OutOfStorage, 'Out of storage')
            }

            throw new UploadError(UploadFailedReason.NetworkError, 'Network error')
        }
    }

    public async deleteFile(jobID: JobID, fileID: FileID) {
        const { commonParams, requestOptions } = this.getRequestContext(jobID)
        const response = await deleteRequestJobsFilesByIdByJobUuidAndFileUuid(
            jobID,
            fileID,
            commonParams,
            requestOptions,
        )

        return response.data
    }

    public async emptyTrashCan(fileid: FileID): Promise<void> {
        const { commonParams, requestOptions } = this.getRequestContext()
        await postRequestEmptyTrashCan(
            {
                ...commonParams,
                fileid,
            },
            requestOptions,
        )
    }

    public async restoreMultipleFiles(
        jobID: JobID,
        fileIDs: FileID[],
        onProgress?: (progress: number) => void,
    ): Promise<FileActionMultiResponseDTO> {
        const { commonParams, requestOptions } = this.getRequestContext(jobID)
        // 200 fileIDs max for each multiRollbackPost from backend end point,
        // split fileIDs into chunks with max 200 fileIDs in each chunk
        const fileIDsChunks = chunks(fileIDs, 200)
        const chunkCount = fileIDsChunks.length
        const responses: FileActionMultiResponseDTO[] = []

        let finished = 0
        await managedPromiseAll(fileIDsChunks, async (chunk) => {
            try {
                const response = await postRequestJobsMultiRollbackByJobUuid(
                    jobID,
                    { file_uuids: chunk },
                    commonParams,
                    requestOptions,
                )
                responses.push(response.data)
            } catch (e) {
                console.error('Failed to restore files in batch:', e)
                responses.push({
                    results: chunk.map((fileID) => ({
                        file_uuid: fileID,
                        status: FileActionStatusDTO.error,
                    })),
                    quota: undefined,
                })
            } finally {
                finished += 1
                if (onProgress) {
                    onProgress(finished / chunkCount)
                }
            }
        })

        return responses.reduce(
            (acc, r) => {
                const results = acc.results.concat(r.results)

                let quota = acc.quota

                if (r.quota !== undefined) {
                    quota = {
                        used_space: Math.max(acc.quota?.used_space ?? 0, r.quota.used_space || 0),
                        max_space: r.quota.max_space,
                    }
                }

                return {
                    results,
                    quota,
                }
            },
            {
                results: [],
                quota: undefined,
            } as FileActionMultiResponseDTO,
        )
    }

    /** Restores all albums in trash  */
    public async restoreAlbums(jobIDs: string[]): Promise<RestoreAlbumsResponseDTO> {
        const { commonParams, requestOptions } = this.getRequestContext()
        const response = await postRequestRollbackAlbums(
            {
                job_uuids: jobIDs,
            },
            commonParams,
            requestOptions,
        )

        return response.data
    }

    /** Permanently deletes all albums in trash */
    public async emptyTrashCanAlbums(jobIDs: string[]): Promise<DeleteAlbumsResponseDTO> {
        const { commonParams, requestOptions } = this.getRequestContext()
        const response = await deleteRequestDeleteAlbumsFromTrashCan(
            {
                job_uuids: jobIDs,
            },
            commonParams,
            requestOptions,
        )

        return response.data
    }

    public async addComment(
        jobID: JobID,
        fileID: string,
        comment: string,
    ): Promise<CommentResponseDTO> {
        const { commonParams, requestOptions } = this.getRequestContext(jobID)
        const response = await postRequestJobsFilesByIdCommentsByJobUuidAndFileUuid(
            jobID,
            fileID,
            {
                ...commonParams,
                comment,
            },
            requestOptions,
        )

        return response.data
    }

    public async deleteComment(
        jobID: JobID,
        fileID: string,
        commentID: CommentID,
    ): Promise<CommentResponseDTO> {
        const { commonParams, requestOptions } = this.getRequestContext(jobID)
        const response = await deleteRequestJobsFilesByIdCommentsByJobUuidAndFileUuidAndCommentUuid(
            jobID,
            fileID,
            commentID,
            commonParams,
            requestOptions,
        )

        return response.data
    }

    public async editComment(
        jobID: JobID,
        fileID: string,
        commentID: CommentID,
        commentText: string,
    ): Promise<CommentResponseDTO> {
        const { commonParams, requestOptions } = this.getRequestContext(jobID)
        const response = await putRequestJobsFilesByIdCommentsByJobUuidAndFileUuidAndCommentUuid(
            jobID,
            fileID,
            commentID,
            {
                ...commonParams,
                comment: commentText,
            },
            requestOptions,
        )

        return response.data
    }

    public async subscribeToJob(jobID: JobID) {
        const { commonParams, requestOptions } = this.getRequestContext(jobID)
        await postRequestJobsSubscribeByJobUuid(jobID, commonParams, requestOptions)
    }

    public async unsubscribeFromJob(jobID: JobID) {
        const { commonParams, requestOptions } = this.getRequestContext(jobID)
        await postRequestJobsUnsubscribeByJobUuid(jobID, commonParams, requestOptions)
    }

    public static getDownloadOptions(
        type: 'download' | 'export' | 'takeout',
        zipFileName: string,
        convertHEIC: boolean = false,
    ) {
        switch (type) {
            case 'download':
                return {
                    flattened: '1',
                    heic_to_jpeg: convertHEIC ? '1' : '0',
                    master_only: '1',
                    include_subrevisions: '0',
                    zip_filename: zipFileName,
                }
            case 'export':
                return {
                    flattened: '1',
                    heic_to_jpeg: '0',
                    master_only: '0',
                    include_subrevisions: '0',
                    zip_filename: zipFileName,
                }
            case 'takeout':
                return {
                    flattened: '1',
                    heic_to_jpeg: '0',
                    master_only: '0',
                    include_subrevisions: '1',
                    zip_filename: zipFileName,
                }
        }
    }

    /**
     * Downloads files from a specified job based on the download type.
     *
     * @param {'download' | 'export' | 'takeout'} downloadType - The type of download to perform.
     * @param {JobID} jobID - The unique identifier for the job.
     * @param {FileID[]} fileIDs - An array of file IDs to download.
     * @param {boolean} [hasHEIC] - Optional flag to indicate if the files include HEIC format.
     * @param {string} [zipFileName=PRODUCT_NAME] - The name for the resulting ZIP file. Defaults to PRODUCT_NAME.
     * @returns {Promise<void>} - A promise that resolves when the files have been successfully downloaded.
     *
     * @example behavior:
     * 1. downloadType === 'download':
     *     a. single file:
     *         uses `downloadThroughAnchor` with `GET` request to download the file through `a` tag.
     *         download single file thumbnail, for HEIC file it will convert to JPEG.
     *     b. multiple files:
     *         uses `sendPOSTRedirect` with `POST` request with `fileIDs` as body to download the files through `form` submit.
     *         download all files thumbnail as archive, for HEIC file it will convert to JPEG, for bust photos it will download only master photo.
     * 2. downloadType === 'export':
     *     a. single file:
     *         download single file in original format.
     *     b. multiple files:
     *         download all files in original format as archive.
     * 2. downloadType === 'takeout':
     *     a. single file:
     *         download single file in original format.
     *     b. multiple files:
     *         download all files in original format with its subrevisions as archive.
     *
     */
    public async downloadFilesFromJob(
        downloadType: 'download' | 'export' | 'takeout',
        jobID: JobID,
        fileIDs: FileID[],
        hasHEIC?: boolean,
        zipFileName: string = PRODUCT_NAME,
    ): Promise<void> {
        const { commonParams } = this.getRequestContext(jobID)
        if (downloadType === 'download' && hasHEIC === undefined) {
            throw new Error('hasHEIC must be defined for download type')
        }

        if (fileIDs.length === 1) {
            downloadThroughAnchor(
                this.getFilePreviewURL(jobID, fileIDs[0], downloadType === 'download' && hasHEIC),
            )
            return
        }

        return sendPOSTRedirect(
            this.downloadHost.getPath(`/st/4/jobs/${jobID}/files_as_archive`, {
                ...AppService.getDownloadOptions(downloadType, zipFileName, hasHEIC),
                ...commonParams,
            }),
            fileIDs,
        )
    }
    /**
     * Downloads all files from a specified job as an archive based on the download type.
     *
     * @param {'download' | 'export' | 'takeout'} downloadType - The type of download to perform.
     * @param {JobID} jobID - The unique identifier for the job.
     * @param {boolean} [hasHEIC] - Optional flag to indicate if the files include HEIC format.
     * @param {string} [zipFileName=PRODUCT_NAME] - The name for the resulting ZIP file. Defaults to PRODUCT_NAME.
     * @returns {Promise<void>} - A promise that resolves when the archive has been successfully downloaded.
     *
     * @example behavior:
     * 1. downloadType === 'download':
     *     uses `downloadThroughAnchor` with `GET` request to download all files from job through `a` tag.
     *     download all files thumbnail from target job, for HEIC file it will convert to JPEG, for bust photos it will download only master photo.
     * 2. downloadType === 'export':
     *     download all files from target job in original format as archive.
     * 2. downloadType === 'takeout':
     *     download all files from target job in original format with its subrevisions as archive.
     *
     */
    public async downloadJobAsArchive(
        downloadType: 'download' | 'export' | 'takeout',
        jobID: JobID,
        hasHEIC?: boolean,
        zipFileName: string = PRODUCT_NAME,
    ): Promise<void> {
        const { commonParams } = this.getRequestContext(jobID)
        if (downloadType === 'download' && hasHEIC === undefined) {
            throw new Error('hasHEIC must be defined for download type')
        }

        downloadThroughAnchor(
            this.downloadHost.getPath(`/st/4/jobs/${jobID}/files_as_archive`, {
                ...AppService.getDownloadOptions(downloadType, zipFileName, hasHEIC),
                ...commonParams,
            }),
        )
    }

    public async getFileBlobFromId(jobID: JobID, fileID: FileID, toJpeg?: boolean): Promise<Blob> {
        const { commonParams, requestOptions } = this.getRequestContext(jobID)
        const response = await getRequestJobsFilesByIdByJobUuidAndFileUuid(
            jobID,
            fileID,
            undefined,
            {
                ...commonParams,
                to_jpeg: toJpeg,
            },
            { ...requestOptions, responseType: 'blob' },
        )

        return response.data as unknown as Blob
    }

    public getFilePreviewURL(jobID: JobID, fileID: FileID, thumbnail = false): URLstring {
        const { commonParams } = this.getRequestContext(jobID)
        return this.hostUrl.getPath(`/st/4/jobs/${jobID}/files_by_id/${fileID}`, {
            ...commonParams,
            ...(thumbnail ? { to_jpeg: '1' } : undefined),
        })
    }

    public downloadFilesFromTrashAsArchive(
        jobID: JobID,
        files: FileID[],
        zipFileName: string,
    ): Promise<void> {
        const { commonParams } = this.getRequestContext(jobID)
        return sendPOSTRedirect(
            this.downloadHost.getPath(`/st/4/jobs/${jobID}/files_from_trash_as_archive`, {
                flattened: 1,
                zip_filename: zipFileName,
                ...commonParams,
            }),
            files,
        )
    }

    public async setJobName(jobID: JobID, name: string) {
        const { commonParams, requestOptions } = this.getRequestContext(jobID)
        await postRequestJobsNameByJobUuid(
            jobID,
            {
                ...commonParams,
                name,
            },
            requestOptions,
        )
    }

    public async setCoverPhoto(jobID: JobID, fileID: FileID) {
        const { commonParams, requestOptions } = this.getRequestContext(jobID)
        await postRequestJobsCoverByJobUuid(
            jobID,
            {
                ...commonParams,
                id: fileID,
            },
            requestOptions,
        )
    }

    public async setPermissionsforJob(
        jobID: JobID,
        permissions: ExtraJobQueryParamsOf<PostRequestJobsPermissionsByJobUuidParams>,
    ): Promise<JobSetPermissionsResponseDTO> {
        const { commonParams, requestOptions } = this.getRequestContext(jobID)
        const response = await postRequestJobsPermissionsByJobUuid(
            jobID,
            {
                ...commonParams,
                ...permissions,
            },
            requestOptions,
        )

        return response.data
    }

    public async setPrivacyModeForJob(
        jobID: JobID,
        mode: PrivacyModeDTO,
    ): Promise<JobSetPermissionsResponseDTO> {
        const { commonParams, requestOptions } = this.getRequestContext(jobID)
        const response = await postRequestJobsPrivacyModeByJobUuid(
            jobID,
            {
                ...commonParams,
                mode,
            },
            requestOptions,
        )

        return response.data
    }

    public async loveFile(jobID: JobID, fileID: FileID): Promise<SetReactionResponseDTO> {
        const { commonParams, requestOptions } = this.getRequestContext(jobID)
        const response = await postRequestJobsFilesByIdReactionByJobUuidAndFileUuid(
            jobID,
            fileID,
            {
                ...commonParams,
                reaction: ReactionTypeDTO.love,
            },
            requestOptions,
        )

        return response.data
    }

    public async unLoveFile(jobID: JobID, fileID: FileID): Promise<SetReactionResponseDTO> {
        /**
         * This endpoint call removes the file reaction, regardless of which
         * We currently assume that there is only one reaction per file:
         * - You can favourite timeline files
         * - You can love album files
         */
        const { commonParams, requestOptions } = this.getRequestContext(jobID)
        const response = await deleteRequestJobsFilesByIdReactionByJobUuidAndFileUuid(
            jobID,
            fileID,
            commonParams,
            requestOptions,
        )

        return response.data
    }

    public async getUserGrants() {
        const { commonParams, requestOptions } = this.getRequestContext()
        const response = await getRequestUserGrants(commonParams, requestOptions)

        return response.data.result.grants as UserGrant[]
    }

    public executeGrantLink(link: string) {
        return customFetch(link, { method: 'POST' })
    }

    public async setAlbumAttribute(albumID: JobID, attributeName: string, attributeValue: string) {
        const { commonParams, requestOptions } = this.getRequestContext(albumID)
        return putRequestJobsAttributeByJobUuidAndAttribute(
            albumID,
            attributeName,
            {
                ...commonParams,
                value: attributeValue,
            },
            requestOptions,
        )
    }

    public async getDataProtectionConsentValues() {
        const { commonParams, requestOptions } = this.getRequestContext()
        const response = await getRequestDataProtectionConsentValues(commonParams, requestOptions)

        // we unwrap the data from the response, datav2 is used in mobile client
        return response.data.data as DataProtectionConsentData
    }

    public async updateDataProtectionConsentValues(values: Partial<DataProtectionConsentData>) {
        const { commonParams, requestOptions } = this.getRequestContext()
        const response = await postRequestDataProtectionConsentValues(
            values,
            commonParams,
            requestOptions,
        )

        return response.data as DataProtectionUpdateRequestResult
    }

    public async getRequestDataAccessAvailability(): Promise<DataProtectionRequestResultDTO> {
        // Throws 503 if call to request_data_access will yield error due to rate-limiting
        const { commonParams, requestOptions } = this.getRequestContext()
        const response = await getRequestDataProtectionRequestDataAccessAvailability(
            commonParams,
            requestOptions,
        )

        return response.data
    }

    public downloadDataProtectionRequestedDataAccess() {
        window.location.href = this.hostUrl.getPath('/st/4/data_protection/request_data_access')
    }

    public async sendDataProtectionRequestForAccountDeletion() {
        const { commonParams, requestOptions } = this.getRequestContext()
        return postRequestDataProtectionRequestAccountDeletion(commonParams, requestOptions)
    }

    public async postUserStatistics(events: PostRequestUserStatisticsEventsBodiesEventsItem[]) {
        const { commonParams, requestOptions } = this.getRequestContext()
        return postRequestUserStatisticsEvents({ events }, commonParams, requestOptions)
    }

    public async postClientStats(client_status: ClientStats) {
        const { commonParams, requestOptions } = this.getRequestContext()
        return postRequestClientStats(client_status, commonParams, requestOptions)
    }

    public async approveTOS(tos_version: string) {
        const { commonParams, requestOptions } = this.getRequestContext()
        return postRequestApproveTos(
            {
                ...commonParams,
                tos_version,
            },
            requestOptions,
        )
    }

    public async getFullScreenCards(): Promise<FullScreenCardsResponseV2DTO> {
        const { commonParams, requestOptions } = this.getRequestContext()
        const response = await getRequestV2FullScreenCards(commonParams, requestOptions)
        return response.data
    }

    public async dismissFullScreenCard(cardId: string) {
        const { commonParams, requestOptions } = this.getRequestContext()
        return postRequestFullScreenCardsDismissByCardId(cardId, commonParams, requestOptions)
    }

    public async getChangesAndAsyncUploadStatus(
        jobID: JobID,
        since: number,
    ): Promise<ChangesAndAsyncUploadStatus> {
        const { commonParams, requestOptions } = this.getRequestContext(jobID)
        const response = await getRequestJobsChangesAndAsyncUploadStatusByJobUuid(
            jobID,
            {
                ...commonParams,
                since,
            },
            requestOptions,
        )

        return response.data as unknown as ChangesAndAsyncUploadStatus
    }

    // Test endpoint for deleting stripe data
    public deleteStripeTestData() {
        const { commonParams, requestOptions } = this.getRequestContext()
        return postRequestTestDeleteStripeData(commonParams, requestOptions)
    }

    public async getSupportedExtensions() {
        const { requestOptions } = this.getRequestContext()
        const response = await getRequestSupportedExtensions(requestOptions)

        return response.data
    }

    // User account attributes endpoints
    public async getAccountAttributes(): Promise<UserAccountAttribute[]> {
        const { commonParams, requestOptions } = this.getRequestContext()
        const response = await getRequestAccountAttribute(commonParams, requestOptions)

        return response.data.result as UserAccountAttribute[]
    }

    public async upsertAccountAttributes({
        account_attribute_key,
        value,
    }: UpsertUserAccountAttribute): Promise<UserAccountAttribute[]> {
        const { commonParams, requestOptions } = this.getRequestContext()
        const response = await postRequestAccountAttribute(
            value,
            {
                ...commonParams,
                account_attribute_key,
            },
            requestOptions,
        )

        return response.data.result as UserAccountAttribute[]
    }

    public async deleteAccountAttribute(key: UserAccountAttributeKey) {
        const { commonParams, requestOptions } = this.getRequestContext()
        await deleteRequestAccountAttribute(
            {
                ...commonParams,
                account_attribute_key: key,
            },
            requestOptions,
        )
    }

    public async updateDigitalLegacyOption(
        choice: DigitalLegacyChoiceDTO,
        contact?: DigitalLegacyContactDTO,
    ) {
        const { connectKey } = getCurrentLangDefinition()
        const { commonParams, requestOptions } = this.getRequestContext()
        return postRequestDigitalLegacy(
            contact ? [contact] : [],
            {
                ...commonParams,
                digital_legacy_choice: choice,
                language: connectKey as LanguageDTO,
            },
            requestOptions,
        )
    }
    public async getDigitalLegacyOption() {
        const { commonParams, requestOptions } = this.getRequestContext()
        const response = await getRequestDigitalLegacy(commonParams, requestOptions)

        return response.data as Merge<DigitalLegacyInfoDTO, { choice: DigitalLegacyChoiceDTO }>
    }
    // Test endpoint for getting email identifier for unsubscribing email
    public async getTestEmailIdentifierToUnsubscribe() {
        const { commonParams, requestOptions } = this.getRequestContext()
        const response = await getRequestTestEmailConsentId(commonParams, requestOptions)
        return response.data
    }

    // Test endpoint for making collections data
    public async postMakeCollectionsTestData(job_uuid: JobID, collection_type: CollectionTypeDTO) {
        const { commonParams, requestOptions } = this.getRequestContext(job_uuid)
        const response = await postRequestTestJobsMemoryCollectionsByJobUuidAndCollectionType(
            job_uuid,
            collection_type,
            commonParams,
            requestOptions,
        )
        return response.data
    }

    public async updateCollectionClientEvent(data: CollectionClientEventData) {
        const { commonParams, requestOptions } = this.getRequestContext()
        const response = await postRequestClientEvent(
            {
                events: [
                    {
                        event_type: ClientEventTypeDTO.viewed_collection_image,
                        timestamp: (Date.now() / 1000).toString(),
                        data,
                    },
                ],
            },
            commonParams,
            requestOptions,
        )
        return response.data
    }

    public async updateMemoryClientEvent(data: MemoryClientEventData) {
        const { commonParams, requestOptions } = this.getRequestContext()
        const response = await postRequestClientEvent(
            {
                events: [
                    {
                        event_type: ClientEventTypeDTO.viewed_memory_image,
                        timestamp: (Date.now() / 1000).toString(),
                        data,
                    },
                ],
            },
            commonParams,
            requestOptions,
        )
        return response.data
    }
}
